import React, { useEffect, useState, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { Form, Select, Button, message, Pagination } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faToggleOn, faToggleOff } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { getUsers, resetState } from "../features/cutomers/customerSlice";
import { getGraphDataList } from "../features/curriculam/curriculamSlice";
import { getStates } from "../features/statecity/statecitySlice";
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2canvas from "html2canvas";

Chart.register(...registerables);

const CurriculamStatus = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [viewMode, setViewMode] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [districtOptions, setDistrictOptions] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [vcData, setVcData] = useState([]);
  const [vtData, setVtData] = useState([]);
  const [selectedVC, setSelectedVC] = useState("");
  const [selectedVT, setSelectedVT] = useState("");
  const [vcName, setVcName] = useState("");
  const [loading, setLoading] = useState(false);
  const chartRef = useRef(null);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [chartInstance, setChartInstance] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Redux state selectors
  const statelistState = useSelector((state) => state.statecity.statelist);
  const vclistState = useSelector((state) => state.customer.customers);
  const graphState = useSelector((state) => state.curriculam.graphData);

  // Fetch initial data (unfiltered)
  useEffect(() => {
    dispatch(resetState());
    dispatch(getUsers());
    dispatch(getStates());
    dispatch(getGraphDataList()).then((response) => {
      console.log("Initial Graph Data Response:", response.payload);
      setFilteredTableData(response.payload || []);
    });
  }, [dispatch]);

  // Destroy chart when unmounting or filters change to prevent memory leaks
  useEffect(() => {
    return () => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    };
  }, [chartInstance]);

  // Filter handlers (update options but don't filter data until "Apply Filter" is clicked)
  const handleStateChange = (value) => {
    setSelectedState(value);
    setSelectedDistrict("");
    setSelectedVC("");
    setSelectedVT("");
    setVcName("");
    setVcData([]);
    setVtData([]);
    setLoading(true);
    try {
      const districts = statelistState.filter(
        (district) => district.state === value
      );
      setDistrictOptions(districts);
      const filteredVC = vclistState.filter(
        (customer) => customer.state === value && customer.role === "vc"
      );
      setVcData(filteredVC);
    } catch (error) {
      message.error("Failed to fetch districts.");
    } finally {
      setLoading(false);
    }
  };

  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedVC("");
    setSelectedVT("");
    setVcName("");
    const filteredVC = vclistState.filter(
      (customer) =>
        customer.district === value &&
        customer.role === "vc" &&
        customer.state === selectedState
    );
    setVcData(filteredVC);
    const filteredVT = vclistState.filter(
      (customer) =>
        customer.district === value &&
        customer.role === "vt" &&
        customer.state === selectedState
    );
    setVtData(filteredVT);
  };

  const handleVcChange = (value, option) => {
    setSelectedVC(value);
    setSelectedVT("");
    setVcName(option.name);
    const filteredVT = vclistState.filter(
      (customer) => customer.vcuser_id === value && customer.role === "vt"
    );
    setVtData(filteredVT);
  };

  const handleVtChange = (value) => {
    setSelectedVT(value);
    const vt = vtData.find((item) => item._id === value);
    if (vt) {
      setSelectedVT(vt.name || value);
    }
  };

  // Apply filters only on "Apply Filter" click
  const handleSubmit = () => {
    setLoading(true);
    const query = `state_code=${selectedState}&district=${selectedDistrict}&vc_name=${vcName}&vt_name=${selectedVT}`;
    dispatch(getGraphDataList(query))
      .then((response) => {
        console.log("Filtered Graph Data Response:", response.payload);
        const data = response.payload || [];
        setFilteredTableData(data);
        if (data.length > 0) {
          renderChart(data); // Render chart only if there is data
        } else {
          if (chartInstance) {
            chartInstance.destroy();
            setChartInstance(null);
          }
        }
      })
      .catch((error) => {
        message.error("Failed to fetch filtered data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Reset filters to initial state
  const resetFilters = () => {
    form.resetFields();
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedVC("");
    setSelectedVT("");
    setVcName("");
    setVcData([]);
    setVtData([]);
    setCurrentPage(1);
    setLoading(true);
    dispatch(getGraphDataList())
      .then((response) => {
        console.log("Reset Graph Data Response:", response.payload);
        const data = response.payload || [];
        setFilteredTableData(data);
        if (data.length > 0) {
          renderChart(data); // Render chart only if there is data
        } else {
          if (chartInstance) {
            chartInstance.destroy();
            setChartInstance(null);
          }
        }
      })
      .catch((error) => {
        message.error("Failed to reset filters.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Render chart function (called only when there is data)
  const renderChart = (data) => {
    const ctx = chartRef.current?.getContext("2d");
    if (!ctx) return;

    if (chartInstance) {
      chartInstance.destroy();
    }

    const labels = data.map((item) =>
      selectedVC
        ? item.vt_name || "Unknown VT"
        : selectedDistrict
        ? item.vc_name || "Unknown VC"
        : selectedState
        ? item.district || "Unknown District"
        : item.state_name || "Unknown State"
    );
    const assignedData = data.map((item) => item.total_pending || 0);
    const completedData = data.map((item) => item.total_completed || 0);

    const newChartInstance = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Assigned Class",
            data: assignedData,
            backgroundColor: "#f44336",
          },
          {
            label: "Completed Class",
            data: completedData,
            backgroundColor: "#4caf50",
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.raw;
                const assigned =
                  newChartInstance.data.datasets[0].data[context.dataIndex];
                return viewMode
                  ? `${((value / assigned) * 100 || 0).toFixed(2)}%`
                  : value;
              },
            },
          },
          legend: { display: true, position: "top" },
        },
        scales: { y: { beginAtZero: true } },
      },
    });

    setChartInstance(newChartInstance);
  };

  // Pagination logic
  const paginatedData = filteredTableData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Export as CSV (All Table Data)
  const exportAsCSV = () => {
    let headers = [];
    let csvRows = [];

    // Define headers based on selection
    if (selectedVC) {
      headers = [
        "VT Name",
        "Role",
        "State",
        "District",
        "Total Class Assigned",
        "Class Completed",
        "Completion Percentage",
      ];
      csvRows = filteredTableData.map((item) => [
        `"${item.vt_name || "N/A"}"`,
        "VT",
        `"${item.state_name || selectedState || "N/A"}"`,
        `"${item.district || selectedDistrict || "N/A"}"`,
        item.total_pending || 0,
        item.total_completed || 0,
        item.total_pending > 0
          ? `${((item.total_completed / item.total_pending) * 100).toFixed(2)}%`
          : "0%",
      ]);
    } else if (selectedDistrict) {
      headers = [
        "VC Name",
        "Role",
        "State",
        "District",
        "Total Class Assigned",
        "Class Completed",
        "Completion Percentage",
      ];
      csvRows = filteredTableData.map((item) => [
        `"${item.vc_name || "N/A"}"`,
        "VC",
        `"${item.state_name || selectedState || "N/A"}"`,
        `"${item.district || selectedDistrict || "N/A"}"`,
        item.total_pending || 0,
        item.total_completed || 0,
        item.total_pending > 0
          ? `${((item.total_completed / item.total_pending) * 100).toFixed(2)}%`
          : "0%",
      ]);
    } else if (selectedState) {
      headers = [
        "District",
        "State",
        "Total Class Assigned",
        "Class Completed",
        "Completion Percentage",
      ];
      csvRows = filteredTableData.map((item) => [
        `"${item.district || "N/A"}"`,
        `"${item.state_name || selectedState || "N/A"}"`,
        item.total_pending || 0,
        item.total_completed || 0,
        item.total_pending > 0
          ? `${((item.total_completed / item.total_pending) * 100).toFixed(2)}%`
          : "0%",
      ]);
    } else {
      headers = [
        "State",
        "Total Class Assigned",
        "Class Completed",
        "Completion Percentage",
      ];
      csvRows = filteredTableData.map((item) => [
        `"${item.state_name || "N/A"}"`,
        item.total_pending || 0,
        item.total_completed || 0,
        item.total_pending > 0
          ? `${((item.total_completed / item.total_pending) * 100).toFixed(2)}%`
          : "0%",
      ]);
    }

    // Combine headers and rows into CSV content
    const csvContent = [
      headers.join(","),
      ...csvRows.map((row) => row.join(",")),
    ].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "curriculum_status.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Export as PDF (Chart + Table)
  const exportAsPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = 10;

    // Add Title
    doc.setFontSize(16);
    doc.text("Curriculum Status - Detailed Overview", 14, yPosition);
    yPosition += 10;

    // Capture Chart as Image
    const chartCanvas = chartRef.current;
    if (chartCanvas && filteredTableData.length > 0) {
      html2canvas(chartCanvas, { scale: 2 }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = 180; // Width of the chart image in the PDF (in mm)
        const imgHeight = (canvas.height * imgWidth) / canvas.width; // Maintain aspect ratio

        if (yPosition + imgHeight > pageHeight - 20) {
          doc.addPage();
          yPosition = 10;
        }

        doc.addImage(imgData, "PNG", 14, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 10;

        // Add Table Data (All Data, Not Paginated)
        const headers = [];
        const body = [];

        // Define headers based on selection
        if (selectedVC) {
          headers.push([
            "VT Name",
            "Role",
            "State",
            "District",
            "Total Class Assigned",
            "Class Completed",
            "Completion Percentage",
          ]);
          body.push(
            ...filteredTableData.map((item) => [
              item.vt_name || "N/A",
              "VT",
              item.state_name || selectedState || "N/A",
              item.district || selectedDistrict || "N/A",
              item.total_pending || 0,
              item.total_completed || 0,
              item.total_pending > 0
                ? `${(
                    (item.total_completed / item.total_pending) *
                    100
                  ).toFixed(2)}%`
                : "0%",
            ])
          );
        } else if (selectedDistrict) {
          headers.push([
            "VC Name",
            "Role",
            "State",
            "District",
            "Total Class Assigned",
            "Class Completed",
            "Completion Percentage",
          ]);
          body.push(
            ...filteredTableData.map((item) => [
              item.vc_name || "N/A",
              "VC",
              item.state_name || selectedState || "N/A",
              item.district || selectedDistrict || "N/A",
              item.total_pending || 0,
              item.total_completed || 0,
              item.total_pending > 0
                ? `${(
                    (item.total_completed / item.total_pending) *
                    100
                  ).toFixed(2)}%`
                : "0%",
            ])
          );
        } else if (selectedState) {
          headers.push([
            "District",
            "State",
            "Total Class Assigned",
            "Class Completed",
            "Completion Percentage",
          ]);
          body.push(
            ...filteredTableData.map((item) => [
              item.district || "N/A",
              item.state_name || selectedState || "N/A",
              item.total_pending || 0,
              item.total_completed || 0,
              item.total_pending > 0
                ? `${(
                    (item.total_completed / item.total_pending) *
                    100
                  ).toFixed(2)}%`
                : "0%",
            ])
          );
        } else {
          headers.push([
            "State",
            "Total Class Assigned",
            "Class Completed",
            "Completion Percentage",
          ]);
          body.push(
            ...filteredTableData.map((item) => [
              item.state_name || "N/A",
              item.total_pending || 0,
              item.total_completed || 0,
              item.total_pending > 0
                ? `${(
                    (item.total_completed / item.total_pending) *
                    100
                  ).toFixed(2)}%`
                : "0%",
            ])
          );
        }

        // Add Table to PDF
        doc.autoTable({
          head: headers,
          body: body,
          startY: yPosition,
          theme: "striped",
          headStyles: { fillColor: [40, 167, 69] }, // Green header
          styles: { fontSize: 8, cellPadding: 2 },
          margin: { left: 14, right: 14 },
          didDrawPage: (data) => {
            yPosition = data.cursor.y;
          },
        });

        // Save the PDF
        doc.save("curriculum_status.pdf");
      });
    } else {
      // If no chart, just export the table
      const headers = [];
      const body = [];

      if (selectedVC) {
        headers.push([
          "VT Name",
          "Role",
          "State",
          "District",
          "Total Class Assigned",
          "Class Completed",
          "Completion Percentage",
        ]);
        body.push(
          ...filteredTableData.map((item) => [
            item.vt_name || "N/A",
            "VT",
            item.state_name || selectedState || "N/A",
            item.district || selectedDistrict || "N/A",
            item.total_pending || 0,
            item.total_completed || 0,
            item.total_pending > 0
              ? `${((item.total_completed / item.total_pending) * 100).toFixed(
                  2
                )}%`
              : "0%",
          ])
        );
      } else if (selectedDistrict) {
        headers.push([
          "VC Name",
          "Role",
          "State",
          "District",
          "Total Class Assigned",
          "Class Completed",
          "Completion Percentage",
        ]);
        body.push(
          ...filteredTableData.map((item) => [
            item.vc_name || "N/A",
            "VC",
            item.state_name || selectedState || "N/A",
            item.district || selectedDistrict || "N/A",
            item.total_pending || 0,
            item.total_completed || 0,
            item.total_pending > 0
              ? `${((item.total_completed / item.total_pending) * 100).toFixed(
                  2
                )}%`
              : "0%",
          ])
        );
      } else if (selectedState) {
        headers.push([
          "District",
          "State",
          "Total Class Assigned",
          "Class Completed",
          "Completion Percentage",
        ]);
        body.push(
          ...filteredTableData.map((item) => [
            item.district || "N/A",
            item.state_name || selectedState || "N/A",
            item.total_pending || 0,
            item.total_completed || 0,
            item.total_pending > 0
              ? `${((item.total_completed / item.total_pending) * 100).toFixed(
                  2
                )}%`
              : "0%",
          ])
        );
      } else {
        headers.push([
          "State",
          "Total Class Assigned",
          "Class Completed",
          "Completion Percentage",
        ]);
        body.push(
          ...filteredTableData.map((item) => [
            item.state_name || "N/A",
            item.total_pending || 0,
            item.total_completed || 0,
            item.total_pending > 0
              ? `${((item.total_completed / item.total_pending) * 100).toFixed(
                  2
                )}%`
              : "0%",
          ])
        );
      }

      doc.autoTable({
        head: headers,
        body: body,
        startY: yPosition,
        theme: "striped",
        headStyles: { fillColor: [40, 167, 69] },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: 14, right: 14 },
      });

      doc.save("curriculum_status.pdf");
    }
  };

  return (
    <div
      id="curriculum-status-content"
      style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
    >
      <h4
        style={{ fontSize: "large", fontWeight: "bold", marginBottom: "20px" }}
      >
        Curriculum Status - Detailed Overview
      </h4>

      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginBottom: "20px", gap: "10px" }}
      >
        <Form.Item name="state_name">
          <Select
            placeholder="Select State"
            style={{ width: 150 }}
            onChange={handleStateChange}
            value={selectedState}
            allowClear
          >
            {statelistState.map((state, index) => (
              <Select.Option key={index} value={state.code}>
                {state.state_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="district">
          <Select
            placeholder="Select District"
            style={{ width: 160 }}
            onChange={handleDistrictChange}
            loading={loading}
            value={selectedDistrict}
            allowClear
          >
            {districtOptions.flatMap((district) =>
              district.district_name.map((name, idx) => (
                <Select.Option key={idx} value={name}>
                  {name}
                </Select.Option>
              ))
            )}
          </Select>
        </Form.Item>

        <Form.Item name="vc_name">
          <Select
            placeholder="Select VC"
            style={{ width: 150 }}
            onChange={handleVcChange}
            value={selectedVC}
            allowClear
          >
            <Select.Option value="">Select VC</Select.Option>
            {vcData
              .filter((item) => item.role === "vc")
              .map((item) => (
                <Select.Option key={item._id} value={item._id} name={item.name}>
                  {item.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item name="vt_name">
          <Select
            placeholder="Select VT"
            style={{ width: 150 }}
            onChange={handleVtChange}
            value={selectedVT}
            allowClear
          >
            <Select.Option value="">Select VT</Select.Option>
            {vtData
              .filter((item) => item.role === "vt")
              .map((item) => (
                <Select.Option key={item._id} value={item.name}>
                  {item.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <div
            onClick={() => setViewMode((prev) => !prev)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span style={{ fontWeight: "700" }}>Count</span>
            <FontAwesomeIcon
              icon={viewMode ? faToggleOn : faToggleOff}
              style={{
                color: viewMode ? "#6c757d" : "#94938f",
                fontSize: "30px",
              }}
            />
            <span style={{ fontWeight: "700" }}>Percentage</span>
          </div>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            style={{ backgroundColor: "#28a745", color: "#fff" }}
          >
            Apply Filter
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            onClick={resetFilters}
            style={{ backgroundColor: "#dc3545", color: "#fff" }}
          >
            Reset Filter
          </Button>
        </Form.Item>
      </Form>

      <div
        style={{
          marginTop: "20px",
          padding: "10px",
          height: "300px",
          backgroundColor: "#DCDCDC",
          borderRadius: "8px",
          display: filteredTableData.length > 0 ? "block" : "none",
        }}
      >
        <canvas ref={chartRef}></canvas>
      </div>
      {filteredTableData.length === 0 && (
        <p style={{ marginTop: "20px", textAlign: "center" }}>
          No Data Available
        </p>
      )}

      <div
        className="table-container"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0.5rem",
          padding: "1rem",
          marginTop: "1.5rem",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#ddd" }}>
              {selectedVC ? (
                <>
                  <th style={styles.th}>VT Name</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>District</th>
                  <th style={styles.th}>Total Class Assigned</th>
                  <th style={styles.th}>Class Completed</th>
                  <th style={styles.th}>Completion Percentage</th>
                </>
              ) : selectedDistrict ? (
                <>
                  <th style={styles.th}>VC Name</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>District</th>
                  <th style={styles.th}>Total Class Assigned</th>
                  <th style={styles.th}>Class Completed</th>
                  <th style={styles.th}>Completion Percentage</th>
                </>
              ) : selectedState ? (
                <>
                  <th style={styles.th}>District</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>Total Class Assigned</th>
                  <th style={styles.th}>Class Completed</th>
                  <th style={styles.th}>Completion Percentage</th>
                </>
              ) : (
                <>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>Total Class Assigned</th>
                  <th style={styles.th}>Class Completed</th>
                  <th style={styles.th}>Completion Percentage</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item, index) => (
                <tr key={index} style={{ borderBottom: "1px solid #ddd" }}>
                  {selectedVC ? (
                    <>
                      <td style={styles.td}>{item.vt_name || "N/A"}</td>
                      <td style={styles.td}>VT</td>
                      <td style={styles.td}>
                        {item.state_name || selectedState || "N/A"}
                      </td>
                      <td style={styles.td}>
                        {item.district || selectedDistrict || "N/A"}
                      </td>
                      <td style={styles.td}>{item.total_pending || 0}</td>
                      <td style={styles.td}>{item.total_completed || 0}</td>
                      <td style={styles.td}>
                        {item.total_pending > 0
                          ? `${(
                              (item.total_completed / item.total_pending) *
                              100
                            ).toFixed(2)}%`
                          : "0%"}
                      </td>
                    </>
                  ) : selectedDistrict ? (
                    <>
                      <td style={styles.td}>{item.vc_name || "N/A"}</td>
                      <td style={styles.td}>VC</td>
                      <td style={styles.td}>
                        {item.state_name || selectedState || "N/A"}
                      </td>
                      <td style={styles.td}>
                        {item.district || selectedDistrict || "N/A"}
                      </td>
                      <td style={styles.td}>{item.total_pending || 0}</td>
                      <td style={styles.td}>{item.total_completed || 0}</td>
                      <td style={styles.td}>
                        {item.total_pending > 0
                          ? `${(
                              (item.total_completed / item.total_pending) *
                              100
                            ).toFixed(2)}%`
                          : "0%"}
                      </td>
                    </>
                  ) : selectedState ? (
                    <>
                      <td style={styles.td}>{item.district || "N/A"}</td>
                      <td style={styles.td}>
                        {item.state_name || selectedState || "N/A"}
                      </td>
                      <td style={styles.td}>{item.total_pending || 0}</td>
                      <td style={styles.td}>{item.total_completed || 0}</td>
                      <td style={styles.td}>
                        {item.total_pending > 0
                          ? `${(
                              (item.total_completed / item.total_pending) *
                              100
                            ).toFixed(2)}%`
                          : "0%"}
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={styles.td}>{item.state_name || "N/A"}</td>
                      <td style={styles.td}>{item.total_pending || 0}</td>
                      <td style={styles.td}>{item.total_completed || 0}</td>
                      <td style={styles.td}>
                        {item.total_pending > 0
                          ? `${(
                              (item.total_completed / item.total_pending) *
                              100
                            ).toFixed(2)}%`
                          : "0%"}
                      </td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={
                    selectedVC
                      ? 7
                      : selectedDistrict
                      ? 7
                      : selectedState
                      ? 5
                      : 4
                  }
                  style={{ textAlign: "center", padding: "10px" }}
                >
                  No Data Available
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredTableData.length}
          onChange={handlePageChange}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          onShowSizeChange={handlePageSizeChange}
          style={{ marginTop: "20px", textAlign: "right" }}
        />
      </div>

      <div style={{ marginTop: "20px", display: "flex", gap: "30px" }}>
        <Button
          onClick={exportAsCSV}
          style={{
            backgroundColor: "#28a745",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Export as CSV
        </Button>
        <Button
          onClick={exportAsPDF}
          style={{
            backgroundColor: "#28a745",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Export as PDF
        </Button>
      </div>
    </div>
  );
};

const styles = {
  th: { padding: "10px", textAlign: "left", fontWeight: "bold" },
  td: { padding: "10px", textAlign: "left" },
};

export default CurriculamStatus;
